import { useDialogContext } from '@/components/contexts/ModalContext';
import { useSuspenseHttpQuery } from '@/resources/http/useHttpQuery';
import { t, Trans } from '@lingui/macro';
import { compareByBoolean, compareByStringKey, formatDecimal, formatRelativeTime, isPresent } from '@luminovo/commons';
import {
    chainComparators,
    Chip,
    colorSystem,
    createColumnHelper,
    DestructivePrimaryButton,
    DestructiveSecondaryButton,
    Dialog,
    DialogContent,
    FieldDateControlled,
    FieldMultiSelect,
    FieldSelect,
    FieldTextControlled,
    Flexbox,
    FormItem,
    PrimaryButton,
    SecondaryButton,
    Table,
    Tag,
    TanStackTable,
    Text,
    useTanStackTable,
} from '@luminovo/design-system';
import { QuoteRequestDTO, QuoteRequestField, QuoteRequestLineItemDTO } from '@luminovo/http-client';
import {
    alwaysRequiredFields,
    formatQuantity,
    formatQuoteRequestField,
    formatSupplierDTO,
    QuoteRequestConfigurationFields,
} from '@luminovo/sourcing-core';
import { Delete, Edit } from '@mui/icons-material';
import { Box, Divider, Tooltip } from '@mui/material';
import React from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { FormContainer } from '../../../../../components/formLayouts/FormContainer';
import { SubmitButton } from '../../../../../components/formLayouts/SubmitButton';
import { LayoutCard } from '../../../../../components/LayoutCard';
import { useHttpMutation } from '../../../../../resources/mutation/useHttpMutation';
import { NegotiationIpnLabel } from '../../../../Negotiations/components/NegotiationIpnLabel';
import { PartLiteLabel } from '../../../../Negotiations/components/PartLiteLabel';
import { QuoteRequestStatusChip } from '../../../../Negotiations/components/QuoteRequestStatusChip';
import { useQuoteRequestLineItemsByQuoteRequest } from '../../../../Negotiations/hooks/negotiationHandlers';
import { formatPartLite } from '../../../../Negotiations/model/formatPartLite';
import {
    RecentQuoteRequestConfiguration,
    useRecentQuoteRequestConfigurations,
} from '../hooks/useRecentQuoteRequestConfigurations';
import { extractManufacturer } from '../QuoteRequestAssignPage/TableManufacturers';

type FormValues = {
    dueDate: string;
    notes: string;
    fields: QuoteRequestConfigurationFields;
};

type RowDate = QuoteRequestLineItemDTO;

type SharedContext = {
    enableDeleteMode: boolean;
    setEnableDeleteMode: (enable: boolean) => void;
};

const columnHelper = createColumnHelper<RowDate>();

const columns = [
    columnHelper.text('component_origin', {
        id: 'ipn',
        label: () => t`IPN`,
        size: 150,
        cell: ({ row }) => <NegotiationIpnLabel ipn={row.original.component_origin} />,
    }),
    columnHelper.text((row) => formatPartLite(row.requested_part), {
        id: 'requestedPart',
        label: () => t`Requested part`,
        size: 200,
        cell: ({ row }) => <PartLiteLabel variant={'body-small'} part={row.original.requested_part} />,
    }),
    columnHelper.text((row) => extractManufacturer(row.requested_part).name, {
        id: 'manufacturer',
        label: () => t`Manufacturer`,
        size: 100,
        initialVisibility: false,
    }),

    columnHelper.text((row) => row.description ?? '-', {
        id: 'description',
        label: () => t`Description`,
        size: 200,
    }),

    columnHelper.number((row) => row.required_quantity.quantity, {
        id: 'requiredQuantity',
        label: () => t`Required quantity`,
        size: 120,
        cell: ({ row }) => formatQuantity(row.original.required_quantity, { showPiecesUnit: false }),
    }),
];

function TableQuoteRequestLineItems({ data }: { data: QuoteRequestLineItemDTO[] | undefined }) {
    const [enableDeleteMode, setEnableDeleteMode] = React.useState(false);

    const { table } = useTanStackTable({
        data,
        columns,
        enableColumnOrdering: true,
        enableColumnHiding: true,
        enableExcelExport: true,
        enableSelection: {
            enabled: enableDeleteMode,
            getRowId: (row) => String(row.id),
        },
        sharedContext: { enableDeleteMode, setEnableDeleteMode },
    });

    return <TanStackTable table={table} ActionButton={ActionButton} />;
}

function ActionButton({
    table,
    sharedContext,
}: {
    table: Table<QuoteRequestLineItemDTO>;
    sharedContext: SharedContext;
}) {
    const { enableDeleteMode, setEnableDeleteMode } = sharedContext;
    const { mutateAsync, isPending: isLoading } = useHttpMutation('POST /quote-request/line-item/delete', {
        snackbarMessage: t`Quote request line items deleted`,
        onSuccess: () => {
            table.resetRowSelection();
            setEnableDeleteMode(false);
        },
    });

    if (enableDeleteMode) {
        const disabled = !(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected());
        return (
            <Flexbox gap={4}>
                <SecondaryButton
                    size="medium"
                    onClick={() => {
                        table.resetRowSelection();
                        setEnableDeleteMode(false);
                    }}
                >
                    {t`Cancel`}
                </SecondaryButton>
                <Tooltip title={disabled ? t`Select rows to delete` : ''}>
                    <span>
                        <DestructivePrimaryButton
                            size="medium"
                            disabled={!(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected())}
                            startIcon={<Delete />}
                            isLoading={isLoading}
                            onClick={() => {
                                const ids = table.getSelectedRowModel().rows.map((row) => row.original.id);
                                mutateAsync({ requestBody: { ids } });
                            }}
                        >
                            {t`Delete`}
                        </DestructivePrimaryButton>
                    </span>
                </Tooltip>
            </Flexbox>
        );
    }

    return (
        <DestructiveSecondaryButton size="medium" startIcon={<Delete />} onClick={() => setEnableDeleteMode(true)}>
            {t`Delete line items`}
        </DestructiveSecondaryButton>
    );
}

function FormItemDueDate() {
    const { control } = useFormContext<FormValues>();
    const { errors } = useFormState<FormValues>();
    const dueDate = useWatch({ control, name: 'dueDate' });

    return (
        <FormItem label={t`Due date`}>
            <FieldDateControlled
                control={control}
                name="dueDate"
                inFuture
                minDate={new Date().toISOString()}
                FieldProps={{
                    placeholder: t`Select due date...`,
                }}
            />
            <Text>{!isPresent(errors.dueDate) && Boolean(dueDate) && formatRelativeTime(dueDate)}</Text>
        </FormItem>
    );
}

function FormItemNotes() {
    const { control } = useFormContext<FormValues>();
    return (
        <FormItem label={t`Notes`}>
            <FieldTextControlled
                control={control}
                name="notes"
                FieldProps={{
                    placeholder: t`Add notes...`,
                    multiline: true,
                    minRows: 2,
                }}
            />
        </FormItem>
    );
}

export function QuoteRequestDraftView({ quoteRequestId }: { quoteRequestId: string }) {
    const {
        data: { quoteRequest, defaultValues },
    } = useSuspenseHttpQuery(
        'GET /quote-request/:id',
        {
            pathParams: { id: quoteRequestId },
        },
        {
            select: (
                data,
            ): {
                quoteRequest: QuoteRequestDTO;
                defaultValues: FormValues;
            } => ({
                quoteRequest: data,
                defaultValues: {
                    dueDate: data.due_date ?? '',
                    notes: data.notes ?? '',
                    fields: new QuoteRequestConfigurationFields({
                        hidden: Array.from(data.configuration?.hidden_fields ?? []),
                        required: Array.from(data.configuration?.required_fields ?? []),
                    }),
                },
            }),
        },
    );

    const { data: quoteRequestLineItems } = useQuoteRequestLineItemsByQuoteRequest(quoteRequest.id);

    const { mutateAsync } = useHttpMutation('PATCH /quote-request/:id', {
        snackbarMessage: t`Quote request updated`,
    });

    const onSubmit = (values: FormValues) => {
        mutateAsync({
            pathParams: { id: quoteRequest.id },
            requestBody: {
                due_date: Boolean(values.dueDate) ? values.dueDate : null,
                notes: Boolean(values.notes) ? values.notes : null,
                hidden_fields: Array.from(values.fields.hidden),
                required_fields: Array.from(values.fields.required),
            },
        });
    };

    return (
        <FormContainer defaultValues={defaultValues} onSubmit={onSubmit}>
            <Box display="grid" gridTemplateColumns="300px 5fr" gap={'16px'} height={'100%'}>
                <Flexbox flexDirection="column" gap="16px">
                    <LayoutCard>
                        <Flexbox justifyContent="space-between" gap={8}>
                            <Text variant="h3" showEllipsis>
                                {formatSupplierDTO(quoteRequest.supplier)}
                            </Text>
                            <Tag attention="low" color={'neutral'} label={`#${quoteRequest.number}`} />
                        </Flexbox>
                        <Flexbox flexDirection="column" rowGap={1}>
                            <Flexbox justifyContent="space-between" gap={8}>
                                <Text
                                    variant="body-small"
                                    color={colorSystem.neutral[8]}
                                >{t`Line items to be sent`}</Text>
                                <QuoteRequestStatusChip status={quoteRequest.status} />
                            </Flexbox>
                            <Text variant="h4">{formatDecimal(quoteRequestLineItems?.length)}</Text>
                        </Flexbox>
                        <Divider />
                        <FormItemDueDate />
                        <FormItemNotes />

                        <FormItemConfigureQuoteRequestFields />
                        <SubmitButton size="small" label={t`Update quote request`} />
                    </LayoutCard>
                </Flexbox>

                <LayoutCard style={{ height: '80vh' }}>
                    <TableQuoteRequestLineItems data={quoteRequestLineItems} />
                </LayoutCard>
            </Box>
        </FormContainer>
    );
}

function FormItemConfigureQuoteRequestFields() {
    const { control, setValue } = useFormContext<FormValues>();
    const fields = useWatch({ control, name: 'fields' });
    const { setDialog, closeDialog } = useDialogContext();

    return (
        <>
            <FormItem
                label={t`Configure fields`}
                actions={
                    <SecondaryButton
                        startIcon={<Edit />}
                        size="small"
                        onClick={() =>
                            setDialog(
                                <Dialog open={true} onClose={closeDialog} maxWidth="md" title={t`Configure fields`}>
                                    <DialogContent>
                                        <ViewConfigureQuoteRequestFields
                                            initialFields={fields}
                                            onSave={({ fields }) => {
                                                setValue('fields', fields);
                                                closeDialog();
                                            }}
                                        />
                                    </DialogContent>
                                </Dialog>,
                            )
                        }
                    >
                        {t`Edit`}
                    </SecondaryButton>
                }
            >
                {fields.format()}
            </FormItem>
        </>
    );
}

function ViewConfigureQuoteRequestFields({
    initialFields,
    onSave,
}: {
    initialFields: QuoteRequestConfigurationFields;
    onSave: ({ fields }: { fields: QuoteRequestConfigurationFields }) => void;
}) {
    const { data: recentConfigurations = [] } = useRecentQuoteRequestConfigurations();
    const allFields = Array.from(initialFields.selectable);
    const [selectedConfiguration, setSelectedConfiguration] = React.useState<RecentQuoteRequestConfiguration | null>(
        null,
    );

    const [fields, setFields] = React.useState<QuoteRequestConfigurationFields>(initialFields);

    const compareFields = chainComparators(
        compareByBoolean((field: QuoteRequestField) => alwaysRequiredFields.includes(field), {
            trueFirst: true,
        }),
        compareByStringKey((field) => formatQuoteRequestField(field)),
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormItem
                label={t`Recent configurations`}
                variant="description-inlined"
                description={t`Select a recent configuration as your starting point, then customize fields as needed.`}
            >
                <FieldSelect
                    options={recentConfigurations}
                    getOptionLabel={(opt) => {
                        return opt.configuration.format();
                    }}
                    renderOption={(opt) => (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1 }}>
                            <Text variant="body-semibold">
                                <Trans>Required fields</Trans>
                            </Text>
                            <Text>{opt.configuration.formatRequired()}</Text>

                            <Text variant="body-semibold">
                                <Trans>Hidden fields</Trans>
                            </Text>
                            <Text>{opt.configuration.formatHidden()}</Text>

                            <Text variant="body-semibold">
                                <Trans>Last used</Trans>
                            </Text>
                            <Text>
                                {formatRelativeTime(opt.lastUsed)}, {opt.count} times
                            </Text>
                        </Box>
                    )}
                    disableClearable
                    value={selectedConfiguration}
                    onChange={(value) => {
                        setSelectedConfiguration(value);
                        setFields(value?.configuration ?? initialFields);
                    }}
                />
            </FormItem>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
                <FormItem
                    label={t`Required fields`}
                    variant="description-inlined"
                    description={t`${fields.required.length} of ${allFields.length}`}
                >
                    <FieldMultiSelect
                        options={allFields}
                        value={Array.from(fields.required).sort(compareFields)}
                        getOptionKey={(opt) => opt}
                        getOptionColor={(opt) => (alwaysRequiredFields.includes(opt) ? 'blue' : 'neutral')}
                        getOptionLabel={(opt) => formatQuoteRequestField(opt)}
                        getOptionDisabled={(opt) => alwaysRequiredFields.includes(opt)}
                        onChange={(values) => {
                            setFields((old) => old.withRequired(values));
                        }}
                    />
                </FormItem>
                <FormItem
                    label={t`Hidden fields`}
                    variant="description-inlined"
                    description={t`${fields.hidden.length} of ${allFields.length}`}
                >
                    <FieldMultiSelect
                        options={allFields}
                        value={Array.from(fields.hidden).sort()}
                        getOptionKey={(opt) => opt}
                        getOptionLabel={(opt) => formatQuoteRequestField(opt)}
                        onChange={(values) => {
                            setFields((old) => old.withHidden(values));
                        }}
                    />
                </FormItem>
                <FormItem
                    label={t`Optional fields`}
                    variant="description-inlined"
                    description={t`${fields.optional.length} of ${allFields.length}`}
                >
                    <FieldMultiSelect
                        sx={{
                            '& .MuiButtonBase-root': {
                                display: 'none',
                            },
                        }}
                        options={allFields}
                        value={Array.from(fields.optional).sort()}
                        getOptionKey={(opt) => opt}
                        getOptionLabel={(opt) => formatQuoteRequestField(opt)}
                        readOnly
                        disableClearable
                        disableCloseOnSelect
                        onChange={() => {}} // No-op since optional fields are inferred
                    />
                </FormItem>
            </Box>
            <Text>
                <Trans>
                    Note: Fields in <Chip style={{ marginTop: -1 }} color="blue" label={t`blue`} /> are always
                    mandatory.
                </Trans>
            </Text>
            <Box display="flex" justifyContent="flex-end">
                <PrimaryButton
                    onClick={() =>
                        onSave({
                            fields,
                        })
                    }
                >
                    {t`Save`}
                </PrimaryButton>
            </Box>
        </Box>
    );
}
